import { Dialog, useAlertEmit, useDialog } from "@fpsoftware/mvp";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { useEventCallback } from "usehooks-ts";
import { Api } from "@/api.config";
import { useProfile } from "@/auth/useProfile";
import { getErrorMessage } from "@/infrastructure/errors";

export function useActiveSubscription() {
  const { profile } = useProfile();

  return useMemo(() => profile.subscription, [profile]);
}

export function useHasActiveSubscription() {
  const sub = useActiveSubscription();

  return !!sub;
}

export function useSubscriberAction<TArgs extends unknown[], TReturn>(
  fn: (...args: TArgs) => TReturn,
) {
  const hasActiveSub = useHasActiveSubscription();
  const dialog = useDialog();

  return useEventCallback((...args: TArgs): TReturn | undefined => {
    if (!hasActiveSub) {
      dialog.open(Dialog.Confirm, {
        title: "Subscription required.",
      });
      return;
    }

    return fn(...args);
  });
}

export function useSubscriptionActions() {
  const emit = useAlertEmit();

  const checkout = useMutation({
    mutationFn: async () => {
      const { data } = await Api.StripeCheckout();
      window.location.href = data.url;
    },
    onError: (e) => emit({ type: "error", content: getErrorMessage(e) }),
  });

  const portal = useMutation({
    mutationFn: async () => {
      const { data } = await Api.StripePortal();
      window.location.href = data.url;
    },
    onError: (e) => emit({ type: "error", content: getErrorMessage(e) }),
  });

  return { checkout, portal };
}
